import React, { useState, useEffect, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { WidgetDataGuard } from '../components/WidgetDataGuard';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WidgetProps, MappingConfig } from '../types';
import { useWidgetFetch } from '../hooks/useWidgetFetch';
import { useDashboardTranslation } from '../i18n';
import { DASHBOARD_LOCALE, resolveCurrency } from '../utils/formatNumber';
import { readColumns, readString } from '../utils/widgetConfig';
import {
  resolveTableColumns, formatCellValue, meetsCompare, type TableColumn, type CellLabels,
} from '../utils/tableColumns';

/** Ancho de widget por debajo del cual se ocultan las columnas secundarias. */
const COMPACT_WIDTH = 520;

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

/** Comparación estable: numérica cuando ambos lados lo son, alfabética con locale si no. */
function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const na = Number(a), nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return String(a ?? '').localeCompare(String(b ?? ''), DASHBOARD_LOCALE);
}

interface HeaderProps {
  column: TableColumn;
  isDraggable: boolean;
  sort: SortState;
  onSort: (key: string) => void;
}

const SortableHeader: React.FC<HeaderProps> = ({ column, isDraggable, sort, onSort }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.key });
  const style = isDraggable ? { transform: CSS.Transform.toString(transform), transition } : undefined;
  const isSorted = sort?.key === column.key;

  return (
    <th
      ref={isDraggable ? setNodeRef : undefined}
      style={{ ...style, color: 'var(--dash-text-secondary)', borderColor: 'var(--dash-border)' }}
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
      onPointerDown={isDraggable ? e => e.stopPropagation() : undefined}
      // Sentence case y sin negrita: el encabezado orienta, no compite con el dato.
      className={`group/th px-3 py-2 font-normal border-b text-[11px] whitespace-nowrap ${
        column.align === 'right' ? 'text-right' : 'text-left'
      } ${isDraggable ? 'cursor-grab select-none' : ''} ${isDragging ? 'shadow-md z-10' : ''}`}
      title={column.label}
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={`inline-flex items-center gap-1 hover:opacity-80 ${column.align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {column.label}
        {/* El chevron sólo se ve en la columna activa; en las demás asoma al pasar
            el mouse, para que se sepa que son ordenables sin llenar la fila de flechas. */}
        <span className={isSorted ? 'opacity-100' : 'opacity-0 group-hover/th:opacity-40'}>
          {isSorted && sort?.dir === 'desc'
            ? <ChevronDown className="w-3 h-3" />
            : <ChevronUp className="w-3 h-3" />}
        </span>
      </button>
    </th>
  );
};

/**
 * Widget de tabla.
 *
 * Las columnas se declaran en `config_schema.columns` con etiqueta y tipo. Si el
 * widget no las declara —el caso de todo lo ya sembrado— se deducen del dato,
 * humanizando el nombre del campo; ver `tableColumns.ts`, donde está explicado por
 * qué eso es un último recurso y no la solución.
 *
 * <b>Se conserva el reordenamiento de columnas por arrastre</b> que ya existía: es
 * funcionalidad del producto y el rediseño no la toca.
 */
const TableStockCritico: React.FC<WidgetProps> = ({ definition, period, isEditMode }) => {
  const { t } = useDashboardTranslation();
  const { data, schema, loading, error } = useWidgetFetch<Record<string, unknown>[]>(
    definition.widget_id, period, definition.refresh_policy, definition.field_mapping?.rename
  );

  const tableConfig = definition.config_schema as (MappingConfig & {
    type: 'table';
    columns?: TableColumn[];
    format?: string;
    emptyTitle?: string;
  }) | undefined;

  const rows = useMemo(() => (Array.isArray(data) ? data : data ? [data] : []), [data]);

  // `readColumns` descarta un `columns` que no sea un array de columnas con `key` y `type`:
  // antes un valor mal declarado llegaba como `TableColumn[]` por el cast y rompía más abajo.
  const declaredColumns = useMemo(() => readColumns(definition.config_schema), [definition.config_schema]);

  const columns = useMemo(
    () => resolveTableColumns(
      declaredColumns,
      rows,
      tableConfig?.type === 'table' ? tableConfig.visibleColumns : undefined,
      // El envelope del endpoint: trae las etiquetas de negocio ya escritas.
      schema?.fields,
    ),
    [declaredColumns, tableConfig, rows, schema],
  );

  const columnKeys = useMemo(() => columns.map(c => c.key), [columns]);
  const [localOrder, setLocalOrder] = useState<string[]>(columnKeys);

  // Resincronizar cuando cambian las columnas de origen (p. ej. al cambiar periodo).
  // Se compara por firma: comparar el array por referencia dispararía en cada render.
  const columnsSignature = columnKeys.join('|');
  useEffect(() => {
    setLocalOrder(columnsSignature ? columnsSignature.split('|') : []);
  }, [columnsSignature]);

  /** Ancho del widget, para decidir qué columnas caben. */
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setIsCompact(entry.contentRect.width < COMPACT_WIDTH);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const [sort, setSort] = useState<SortState>(null);

  /**
   * Ordenar es estado de UI, no de query: <b>no dispara `refetch`</b>. El tercer
   * clic vuelve al orden del servidor, que es un estado distinto de "ascendente".
   */
  const handleSort = (key: string) => {
    setSort(current => {
      if (current?.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const visibleColumns = useMemo(() => {
    const byKey = new Map(columns.map(c => [c.key, c]));
    return localOrder
      .map(key => byKey.get(key))
      .filter((c): c is TableColumn => Boolean(c))
      .filter(c => !(isCompact && c.hideOnCompact));
  }, [localOrder, columns, isCompact]);

  const sortedRows = useMemo(() => {
    if (!sort) {
      // Orden del servidor. Si el widget declaró uno por configuración, se respeta.
      if (tableConfig?.type === 'table' && tableConfig.sortField) {
        const field = tableConfig.sortField;
        const dir = tableConfig.sortDirection === 'desc' ? -1 : 1;
        return [...rows].sort((a, b) => compareValues(a[field], b[field]) * dir);
      }
      return rows;
    }
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => compareValues(a[sort.key], b[sort.key]) * dir);
  }, [rows, sort, tableConfig]);

  const currency = resolveCurrency().symbol;

  /**
   * Las etiquetas del booleano, traducidas aquí porque `formatCellValue` es una función pura y no
   * puede llamar al hook. Sin esto, una columna booleana mostraba «Sí»/«No» en un host en inglés.
   */
  const cellLabels = useMemo<CellLabels>(
    () => ({ yes: t('dashboard.table.yes'), no: t('dashboard.table.no') }),
    [t],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = localOrder.indexOf(active.id as string);
    const newIdx = localOrder.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;
    setLocalOrder(arrayMove(localOrder, oldIdx, newIdx));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isDraggable = !isEditMode && visibleColumns.length > 1;

  return (
    <WidgetDataGuard
      loading={loading}
      error={error}
      data={data}
      shape="table"
      emptyTitle={readString(definition.config_schema, 'emptyTitle')}
    >
      <div ref={containerRef} className="w-full h-full flex flex-col">
        <div className="flex-1 overflow-auto custom-scrollbar">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleColumns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-[5]" style={{ background: 'var(--dash-surface-card)' }}>
                  <tr>
                    {visibleColumns.map(col => (
                      <SortableHeader
                        key={col.key}
                        column={col}
                        isDraggable={isDraggable}
                        sort={sort}
                        onSort={handleSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, i) => (
                    <tr
                      key={i}
                      className="border-b transition-colors hover:bg-[var(--dash-surface-page)]"
                      style={{ borderColor: 'var(--dash-border)' }}
                    >
                      {visibleColumns.map(col => {
                        const critical = meetsCompare(col, row);
                        return (
                          <td
                            key={col.key}
                            className={`px-3 py-2 text-[13px] whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                            style={{
                              color: critical ? 'var(--dash-danger)' : 'var(--dash-text-primary)',
                              fontWeight: critical ? 500 : 400,
                              fontVariantNumeric: col.align === 'right' ? 'tabular-nums' : undefined,
                            }}
                          >
                            {/* Color MÁS forma: el punto dice lo mismo que el rojo
                                para quien no distingue el rojo. */}
                            {critical && (
                              <span
                                className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                                style={{ background: 'var(--dash-danger)' }}
                                data-testid="cell-critical-dot"
                              />
                            )}
                            {formatCellValue(row[col.key], col.type, currency, cellLabels)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>

        {/* Pie: cuántas filas se ven. Sin él, tres filas en una tarjeta donde caben
            diez parecen ser todo lo que existe. */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 border-t text-[11px]"
          style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)' }}
          data-testid="table-footer"
        >
          <span>
            {t('dashboard.table.rowCount').replace('{count}', String(sortedRows.length))}
          </span>
          {isCompact && columns.some(c => c.hideOnCompact) && (
            <span>{t('dashboard.table.expandForMore')}</span>
          )}
        </div>
      </div>
    </WidgetDataGuard>
  );
};

export default TableStockCritico;
